import { Spin } from 'antd';
import classNames from 'classnames';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DIAGONAL_CORNER_OPTIONS, SELECTION_CIRCLES, UNIT_MEASURE } from '@common/constants';
import { getCornerType } from '@common/helpers';
import formatUOM from '@common/helpers/formatUOM';
import { DiagonalCornerModel } from '@common/models';
import { COMING_SOON_IMAGE, getProductDescription } from '@common/utils';
import { formatPriceUnitOrFree } from '@common/utils/formatPriceUnitOrFree';
import { SectionSubtitle } from '@core/components';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import ImageUtils from '@core/utils/ImageUtils';
import { useDiagonalCorners } from '@ee-api/diagonal-corners-api/useDiagonalCorners';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { resetSelectedCornerRadius } from '@redux/slices/cornerRadiiSlice';
import { resetSelectedCustomSide } from '@redux/slices/customSideSlice';
import {
    resetSelectedDiagonalCorner,
    selectIsInteriorDiagonalCornerEnabled,
    selectSelectedDiagonalCorner,
    updateIsInteriorDiagonalCornerEnabled,
    updateSelectedDiagonalCorner,
} from '@redux/slices/diagonalCornerSlice';
import {
    checkDiagonalCornerInShapes,
    isDiagonalCornerRemoveEnabled,
} from '@redux/slices/diagonalCornerSlice/diagonalCornerSlice.utils';
import { updateSelectionCircles } from '@redux/slices/fabricSlice';

export interface DiagonalCornerItemProps {
    diagonalCorner: DiagonalCornerModel;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const DiagonalCorners = (): JSX.Element => {
    const { t } = useTranslation();
    const { isFetchingDiagonalCorners, diagonalCorners } = useDiagonalCorners();

    const isInteriorDiagonalCornerEnabled = useAppSelector(selectIsInteriorDiagonalCornerEnabled);
    const selectedDiagonalCorner = useAppSelector(selectSelectedDiagonalCorner);
    const { selectedValue: selectedMaterial } = useSelectedMaterial();

    const { drawingShapes, areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);

    const dispatch = useAppDispatch();

    const { trackSelectDiagonal } = useAnalyticsEvents();

    // used to deselect the 'Remove Diagonal Corner' option when there are no more diagonal corners applied on the drawn shape
    useEffect(() => {
        if (
            selectedDiagonalCorner?.name === DIAGONAL_CORNER_OPTIONS.REMOVE_DIAGONAL_CORNER &&
            !isDiagonalCornerRemoveEnabled(drawingShapes)
        ) {
            dispatch(resetSelectedDiagonalCorner());
        }
    }, [selectedDiagonalCorner, drawingShapes, dispatch]);

    // check if in the array of shapes there are shapes with interior diagonal corners
    useEffect(() => {
        const isInteriorDiagonalCornerInShape = checkDiagonalCornerInShapes(drawingShapes);

        dispatch(updateIsInteriorDiagonalCornerEnabled(isInteriorDiagonalCornerInShape));
    }, [drawingShapes, dispatch]);

    const selectDiagonalCorner = (diagonalCorner: DiagonalCornerModel, wasPreviouslySelected: boolean) => {
        if (wasPreviouslySelected) {
            return;
        }
        const cornerType = getCornerType(diagonalCorner);
        if (cornerType === DIAGONAL_CORNER_OPTIONS.INSIDE_CORNER && !isInteriorDiagonalCornerEnabled) {
            dispatch(resetSelectedDiagonalCorner());
            return;
        }

        if (
            cornerType === DIAGONAL_CORNER_OPTIONS.REMOVE_DIAGONAL_CORNER &&
            !isDiagonalCornerRemoveEnabled(drawingShapes)
        ) {
            return;
        }

        trackSelectDiagonal({ diagonal: diagonalCorner });
        dispatch(updateSelectedDiagonalCorner(diagonalCorner));
        dispatch(updateSelectionCircles(SELECTION_CIRCLES.FOR_DIAGONAL_CORNERS));
        dispatch(resetSelectedCustomSide());
        dispatch(resetSelectedCornerRadius());
    };

    const isCardOptionDisabled = (diagonalCorner: DiagonalCornerModel) => {
        const cornerType = getCornerType(diagonalCorner);

        const firstCondition = cornerType === DIAGONAL_CORNER_OPTIONS.INSIDE_CORNER && !isInteriorDiagonalCornerEnabled;
        const secondCondition =
            cornerType === DIAGONAL_CORNER_OPTIONS.REMOVE_DIAGONAL_CORNER &&
            !isDiagonalCornerRemoveEnabled(drawingShapes);
        return firstCondition || secondCondition;
    };

    const DiagonalCornerItem = (props: DiagonalCornerItemProps) => {
        const { diagonalCorner } = props;
        const description = getProductDescription(diagonalCorner);
        const cornerType = getCornerType(diagonalCorner);

        const unitOfMeasure = formatUOM(diagonalCorner.unitOfMeasure || UNIT_MEASURE.DIAGONAL_CORNER);

        const isSelected = !!(areThereShapesInTheDrawingArea && selectedDiagonalCorner?.name === diagonalCorner?.name);

        return (
            <div
                className={classNames('card-option single-action-card', {
                    selected: isSelected,
                    disabled: isCardOptionDisabled(diagonalCorner),
                })}
                onClick={() => selectDiagonalCorner(diagonalCorner, isSelected)}
            >
                <img
                    alt="diagonal-corner"
                    className="card-option__image"
                    src={diagonalCorner.imageUrl ? ImageUtils.imgUrl(diagonalCorner.imageUrl, 300) : COMING_SOON_IMAGE}
                />
                <div className="name">{description}</div>

                {cornerType !== DIAGONAL_CORNER_OPTIONS.REMOVE_DIAGONAL_CORNER && (
                    <div className="price">{formatPriceUnitOrFree(diagonalCorner.price, unitOfMeasure)}</div>
                )}
            </div>
        );
    };

    const DiagonalCornerContent = () => {
        return isFetchingDiagonalCorners ? (
            <Spin style={{ fontSize: 24 }} />
        ) : (
            <div className="card-single-select">
                {diagonalCorners.map((diagonalCorner, id) => (
                    <DiagonalCornerItem key={id} diagonalCorner={diagonalCorner} />
                ))}
            </div>
        );
    };

    const isDiagonalCornerOptionAvailable = [
        diagonalCorners.length,
        areThereShapesInTheDrawingArea,
        selectedMaterial,
    ].every(Boolean);

    return isDiagonalCornerOptionAvailable ? (
        <>
            <SectionSubtitle
                name={t('SCROLLABLE_AREA.DIAGONAL_CORNER.TITLE')}
                description={t('SCROLLABLE_AREA.DIAGONAL_CORNER.DESCRIPTION')}
                showLine
            />
            <DiagonalCornerContent />
        </>
    ) : (
        <></>
    );
};

export default DiagonalCorners;
